import type { User } from '../types/auth'

/**
 * Admin configuration
 *
 * To add admin access:
 * 1. Set environment variable: VUE_APP_ADMIN_EMAILS=your.email@example.com,another@example.com
 * 2. Or add your email directly in the ADMIN_EMAILS array below (for development)
 *
 * Note: Multiple emails can be separated by commas in the env var
 */
const ADMIN_EMAILS_FROM_ENV = (process.env.VUE_APP_ADMIN_EMAILS || '').split(',').filter(Boolean)

// Add your email here for development (remove before committing sensitive info)
const HARDCODED_ADMIN_EMAILS: string[] = [
  'abhishek.powar@gohighlevel.com',
  'rohit.jain@gohighlevel.com',
  'surya.singh@gohighlevel.com'
]

/**
 * Combined list of admin emails from environment and hardcoded sources
 */
const ADMIN_EMAILS = [...ADMIN_EMAILS_FROM_ENV, ...HARDCODED_ADMIN_EMAILS]
  .map((email) => email.toLowerCase().trim())
  .filter(Boolean)

// Keep group names canonical to match the token's `groups` claim exactly.
const ADMIN_GROUPS = ['Platform Admins']
const ENV_ADMIN_GROUPS = ['Env Admins']

const normalizeEmail = (value: string) => value.toLowerCase().trim()
const normalizeGroup = (value: string) => value.toUpperCase().trim()
const NORMALIZED_HARDCODED_ADMIN_EMAILS = HARDCODED_ADMIN_EMAILS.map(normalizeEmail)

const hasAnyGroup = (user: User | null, allowedGroups: string[]): boolean => {
  if (!user) return false
  const userGroups = user.groups ?? []
  return allowedGroups.some((group) => userGroups.includes(group))
}

// Legacy-safe group matcher for flows that historically accepted
// case-variant IdP group values.
const hasAnyGroupNormalized = (user: User | null, allowedGroups: string[]): boolean => {
  if (!user) return false
  const userGroups = (user.groups ?? []).map(normalizeGroup)
  return allowedGroups.map(normalizeGroup).some((group) => userGroups.includes(group))
}

export const isHardcodedAdminEmail = (user: User | null): boolean => {
  if (!user?.email) return false
  return NORMALIZED_HARDCODED_ADMIN_EMAILS.includes(normalizeEmail(user.email))
}

/**
 * Check if a user is an admin — via email list or the "Platform Admins" group.
 */
export const isAdmin = (user: User | null): boolean => {
  if (!user) return false
  const byEmail = user.email ? ADMIN_EMAILS.includes(normalizeEmail(user.email)) : false
  const byGroup = hasAnyGroup(user, ADMIN_GROUPS)
  return byEmail || byGroup
}

/**
 * Get admin emails (for debugging purposes only)
 */
export const getAdminEmails = (): string[] => {
  return [...ADMIN_EMAILS]
}

export const isPlatformDataLead = (user: User | null): boolean => {
  return hasAnyGroupNormalized(user, ['PLATFORM-DATA-LEAD'])
}

export const isPlatformDataPod = (user: User | null): boolean => {
  return hasAnyGroupNormalized(user, ['PLATFORM-DATA-POD'])
}

/**
 * Environment admins can also access operational tooling in the sidebar.
 */
export const isEnvAdmin = (user: User | null): boolean => {
  return hasAnyGroup(user, ENV_ADMIN_GROUPS)
}

/**
 * Check if a user can edit Cost Explorer configuration.
 * Granted to Platform Admins (via isAdmin), PLATFORM-DATA-LEAD and
 * PLATFORM-DATA-POD group members. Everyone else gets read-only access.
 */
export const isCostExplorerEditor = (user: User | null): boolean => {
  return isAdmin(user) || isPlatformDataLead(user) || isPlatformDataPod(user)
}
